// src/pages/MapPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSwipeable } from 'react-swipeable';
import Header from '../components/Header';
import SidePanel from '../components/SidePanel';
import MapView from '../components/MapView';
import { fetchUpasComStatus } from '../server/Api';
import RoutingService from '../services/RoutingService';
import webSocketService from '../services/WebSocketService';

// Coordenadas padrão se o usuário negar a geolocalização
const DEFAULT_CENTER = [-7.2404146, -35.8883043];

function MapPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [upas, setUpas] = useState([]);
  const [selectedUpa, setSelectedUpa] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [routesData, setRoutesData] = useState({});
  const calculatedRoutesRef = useRef(new Set()); // Rastreia UPAs que já tiveram rotas calculadas

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  useEffect(() => {
    async function loadUpas() {
      const data = await fetchUpasComStatus();
      setUpas(data);
    }
    loadUpas();

    // Conecta ao WebSocket
    webSocketService.connect();

    // Escuta atualizações de todas as UPAs
    const unsubscribe = webSocketService.onAllUpasUpdate((data) => {
      // Atualiza a UPA específica que recebeu atualização
      setUpas(prevUpas => {
        return prevUpas.map(upa => {
          if (upa.id === data.upaId) {
            // Extrai os tempos de espera por classificação
            const waitTimesByClassification = {};
            if (data.data.metricasPorClassificacao) {
              data.data.metricasPorClassificacao.forEach(metrica => {
                const classificacao = metrica.classificacao.toLowerCase();
                waitTimesByClassification[classificacao] = metrica.tempoMedioEsperaMinutos;
              });
            }

            return {
              ...upa,
              totalPacientes: data.data.totalPacientes,
              statusOcupacao: data.data.statusOcupacao,
              averageWaitTime: RoutingService.formatMinutes(data.data.tempoMedioEsperaMinutos),
              queueDetail: {
                blue: data.data.porClassificacao.azul || 0,
                green: data.data.porClassificacao.verde || 0,
                yellow: data.data.porClassificacao.amarelo || 0,
                orange: data.data.porClassificacao.laranja || 0,
                red: data.data.porClassificacao.vermelho || 0,
              },
              waitTimesByClassification: waitTimesByClassification,
            };
          }
          return upa;
        });
      });
    });

    return () => {
      unsubscribe();
      webSocketService.disconnect();
    };
  }, []);

  // Solicita a localização do usuário via navegador
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          });
        },
        (err) => {
          console.error("Erro ao obter localização:", err);
          setUserLocation({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] });
        }
      );
    } else {
      setUserLocation({ lat: DEFAULT_CENTER[0], lng: DEFAULT_CENTER[1] });
    }
  }, []);

  // Calcula rotas com todos os modos de transporte (apenas uma vez por UPA)
  useEffect(() => {
    if (userLocation && upas.length > 0) {
      // Filtra apenas UPAs que ainda não tiveram rotas calculadas
      const upasToCalculate = upas.filter(upa => !calculatedRoutesRef.current.has(upa.id));

      if (upasToCalculate.length === 0) {
        return; // Todas as rotas já foram calculadas
      }

      Promise.all(
        upasToCalculate.map(async (upa) => {
          try {
            // Busca todas as rotas simultaneamente
            const routes = await RoutingService.calculateAllRoutes(
              userLocation.lat,
              userLocation.lng,
              upa.lat,
              upa.lng
            );

            // Busca a geometria da rota
            const osrmUrl = process.env.REACT_APP_OSRM_URL;
            const url = `${osrmUrl}/route/v1/car/${userLocation.lng},${userLocation.lat};${upa.lng},${upa.lat}?overview=full&geometries=geojson`;
            const response = await fetch(url);
            const data = await response.json();

            let coords = null;
            if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
              coords = data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
            }

            // Marca esta UPA como calculada
            calculatedRoutesRef.current.add(upa.id);

            return {
              id: upa.id,
              driving: routes.driving,
              bike: routes.bike,
              foot: routes.foot,
              coords: coords
            };
          } catch (err) {
            console.error(`Erro ao calcular rotas para UPA ${upa.id}:`, err);
            // Marca como calculada mesmo em caso de erro para não tentar novamente
            calculatedRoutesRef.current.add(upa.id);
            return {
              id: upa.id,
              driving: null,
              bike: null,
              foot: null,
              coords: null
            };
          }
        })
      ).then(results => {
        // Mescla os novos resultados com os dados existentes
        setRoutesData(prevData => {
          const newData = { ...prevData };
          results.forEach(item => {
            newData[item.id] = item;
          });
          return newData;
        });
      });
    }
  }, [userLocation, upas]);

  // Calcula o "score" para cada UPA usando tempo de carro
  const { bestUpaId } = useMemo(() => {
    let bestScore = Infinity;
    let bestId = null;
    upas.forEach(upa => {
      const route = routesData[upa.id];
      if (route && route.driving && route.driving.duration) {
        const travelMin = Math.ceil(route.driving.duration / 60);
        const waitMin = parseInt(upa.averageWaitTime.split(" ")[0]);
        const score = travelMin + waitMin;
        if (score < bestScore) {
          bestScore = score;
          bestId = upa.id;
        }
      }
    });
    return { bestUpaId: bestId };
  }, [routesData, upas]);

  
  useEffect(() => {
    if (!selectedUpa && bestUpaId) {
      const best = upas.find(upa => upa.id === bestUpaId);
      setSelectedUpa(best);
    }
  }, [bestUpaId, selectedUpa, upas]);

  const handleSelectUpa = (upa) => {
    setSelectedUpa(upa);
    setSidebarOpen(false);   
  };

  // Configura os handlers de swipe para a sidebar
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setSidebarOpen(false),
    delta: 80, 
    preventDefaultTouchmoveEvent: true,
    trackMouse: false
  });

  return (
    <div className="app-wrapper">
      <Header
        onToggleSidebar={toggleSidebar}
        isSidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      <div className="notification-banner">
        Se você estiver em emergência, procure a unidade mais próxima. Você é prioridade!
      </div>

      <div className="main-content" {...swipeHandlers}>
        <div className={`sidebar ${sidebarOpen ? '' : 'sidebar-closed'}`}>
          <SidePanel
            upas={upas}
            onSelectUpa={handleSelectUpa}
          />
        </div>
        <div className="map-container">
          <MapView
            upas={upas}
            selectedUpa={selectedUpa}
            userLocation={userLocation}
            routesData={routesData}
            bestUpaId={bestUpaId}
            isSidebarOpen={sidebarOpen}
          />
        </div>
      </div>
    </div>
  );
}

export default MapPage;
